import { supabase } from './supabaseClient.js';

// ─────────────────────────────────────────────────────────────────
// Admin data layer. Every query runs under the signed-in admin's auth;
// the is_admin() RLS policies (learners, mood_checkins, decision_traces,
// cohort_concept_memory) grant cross-learner read. Tables WITHOUT an admin
// policy (lesson_progress, learner_state, quiz_attempts, contact_submissions)
// return only the admin's own rows / nothing — those surfaces scaffold.
// Fail soft everywhere: a blocked/empty query returns [] / null, never throws.
// ─────────────────────────────────────────────────────────────────

const PRICE = { early_access: 21.99, direct: 21.99, default: 21.99 };
const INFRA_PER_ACTIVE = 2.0;

// Waitlist form submissions, newest first. Reads under the admin's auth via
// the admin_read_waitlist_submissions RLS policy (is_admin()).
export async function adminWaitlist() {
  const { data, error } = await supabase
    .from('waitlist_submissions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.warn('adminWaitlist:', error.message); return []; }
  return data || [];
}

export async function adminListLearners() {
  // lesson_1_engaged_at ships with the 20260604 migration. Select it when the
  // column exists, but fall back to the base set if the migration hasn't been
  // applied yet — otherwise a missing column would error the WHOLE query and
  // blank the admin console. The Outreach engaged-map also unions the
  // lesson_1_engaged event + completion, so the funnel works either way.
  const BASE = 'id, email, name, first_name, last_name, phone, tier, subscription_status, active_track_id, current_track, is_admin, is_internal, stripe_customer_id, subscription_id, price_id, career_goal, onboarding_completed_at, signup_source, affiliate_id, referral_code, referred_by, created_at';
  let { data, error } = await supabase
    .from('learners')
    .select(`${BASE}, lesson_1_engaged_at`)
    .order('created_at', { ascending: false });
  if (error) {
    ({ data, error } = await supabase
      .from('learners')
      .select(BASE)
      .order('created_at', { ascending: false }));
  }
  if (error) { console.warn('adminListLearners:', error.message); return []; }
  return data || [];
}

export async function adminLatestMoods() {
  // Latest mood per learner — fetch recent mood rows, dedupe client-side.
  const { data, error } = await supabase
    .from('mood_checkins')
    .select('learner_id, mood, score, created_at')
    .eq('kind', 'mood')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) { console.warn('adminLatestMoods:', error.message); return {}; }
  const byLearner = {};
  (data || []).forEach(r => { if (!byLearner[r.learner_id]) byLearner[r.learner_id] = r; });
  return byLearner;
}

export async function adminMoodHistory(learnerId, limit = 7) {
  const { data, error } = await supabase
    .from('mood_checkins')
    .select('mood, score, note, created_at')
    .eq('learner_id', learnerId).eq('kind', 'mood')
    .order('created_at', { ascending: false }).limit(limit);
  if (error) { console.warn('adminMoodHistory:', error.message); return []; }
  return data || [];
}

export async function adminMoodTrend(days = 30) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from('mood_checkins')
    .select('mood, created_at')
    .eq('kind', 'mood').gte('created_at', since)
    .order('created_at', { ascending: true });
  if (error) { console.warn('adminMoodTrend:', error.message); return []; }
  return data || [];
}

export async function adminCohortMemory() {
  const { data, error } = await supabase
    .from('cohort_concept_memory')
    .select('concept_key, lesson_id, track_id, sample_size, pass_rate, difficulty, common_misconception, repair_that_worked, source, refreshed_at')
    .order('sample_size', { ascending: false });
  if (error) { console.warn('adminCohortMemory:', error.message); return []; }
  return data || [];
}

export async function adminDecisionTraces(days = 14) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from('decision_traces')
    .select('created_at, session_phase, resolved_directive, is_demo')
    .gte('created_at', since)
    .order('created_at', { ascending: true });
  if (error) { console.warn('adminDecisionTraces:', error.message); return []; }
  return (data || []).filter(r => !r.is_demo);
}

export async function adminCountDecisionTraces() {
  const { count, error } = await supabase
    .from('decision_traces')
    .select('id', { count: 'exact', head: true });
  if (error) { console.warn('adminCountDecisionTraces:', error.message); return null; }
  return count ?? null;
}

// ── Newly admin-readable surfaces (unlock migration) ─────────────
export async function adminLearnerStates() {
  const { data, error } = await supabase
    .from('learner_state')
    .select('learner_id, current_lesson_id, mastery_score, mastery_state, consecutive_failures, updated_at');
  if (error) { console.warn('adminLearnerStates:', error.message); return []; }
  return data || [];
}

export async function adminLessonTitleMap() {
  const { data, error } = await supabase.from('lessons').select('id, title');
  if (error) { console.warn('adminLessonTitleMap:', error.message); return {}; }
  const m = {}; (data || []).forEach(l => { m[l.id] = l.title; });
  return m;
}

export async function adminProgressRows() {
  const { data, error } = await supabase
    .from('lesson_progress')
    .select('learner_id, status');
  if (error) { console.warn('adminProgressRows:', error.message); return []; }
  return data || [];
}

export async function adminLearnerProgress(learnerId) {
  const { data, error } = await supabase
    .from('lesson_progress')
    .select('status, mastery_level, mastery_score, lessons(title)')
    .eq('learner_id', learnerId);
  if (error) { console.warn('adminLearnerProgress:', error.message); return []; }
  return data || [];
}

export async function adminEvents(days = 30) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from('events')
    .select('event_name, utm_source, utm_medium, utm_campaign, affiliate_code, visitor_id, learner_id, created_at')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(2000);
  if (error) { console.warn('adminEvents:', error.message); return []; }
  return data || [];
}

export async function adminPageViews(days = 90) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from('page_views')
    .select('path, visitor_id, referrer, created_at')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(5000);
  if (error) { console.warn('adminPageViews:', error.message); return []; }
  return data || [];
}

// Permanently delete a learner: all learner-scoped rows, the learners row, and
// the auth user. Routes through api/admin-learner.js (service role), which
// re-verifies the caller is an admin from their session token. Destructive and
// irreversible — the UI must confirm before calling.
export async function adminDeleteLearner(learnerId) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'No active admin session.' };
  try {
    const resp = await fetch('/api/admin-learner', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'delete', learner_id: learnerId }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) return { ok: false, error: body.error || `Request failed (${resp.status}).` };
    return { ok: true, ...body };
  } catch (e) {
    return { ok: false, error: e?.message || 'Network error.' };
  }
}

// Live Stripe finance feed (balance, MRR, subscription mix, recent payouts).
// Routes through api/admin-finance.js, which re-verifies the caller is an admin
// from their session token. Returns { configured, error?, ... } — never throws.
export async function adminStripeFinance() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { configured: false, error: 'No active admin session.' };
  try {
    const resp = await fetch('/api/admin-finance', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) return { configured: false, error: body.error || `Request failed (${resp.status}).` };
    return body;
  } catch (e) {
    return { configured: false, error: e?.message || 'Network error.' };
  }
}

// Send the standard Supabase password-recovery email to a learner. Client-side
// (no service role needed); returns the recovery flow's redirect target.
export async function adminSendPasswordReset(email) {
  if (!email) return { ok: false, error: 'No email on file.' };
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return error ? { ok: false, error: error.message } : { ok: true };
}

// Reliability log rows (admin-only via is_admin() RLS). Defaults to the
// quiz-unavailable fallbacks that power the Support → Quiz Fallback Log.
export async function adminSystemEvents(eventType = 'quiz_unavailable', limit = 200) {
  const { data, error } = await supabase
    .from('system_events')
    .select('user_id, event_type, metadata, created_at')
    .eq('event_type', eventType)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.warn('adminSystemEvents:', error.message); return []; }
  return data || [];
}

export function completionRate(progressRows) {
  if (!progressRows.length) return null;
  const done = progressRows.filter(r => r.status === 'completed').length;
  return Math.round((done / progressRows.length) * 100);
}

// ── Derived dashboards ───────────────────────────────────────────
export function deriveOverview(learners) {
  const total  = learners.length;
  const paid   = learners.filter(l => l.subscription_status === 'active').length;
  const weekAgo = Date.now() - 7 * 86400000;
  const active7 = learners.filter(l => l.updated_at && new Date(l.updated_at).getTime() > weekAgo).length;
  const trialToPaid = total > 0 ? Math.round((paid / total) * 1000) / 10 : 0;
  return { total, paid, active7, trialToPaid };
}

export function deriveRevenue(learners) {
  const paidRows = learners.filter(l => l.subscription_status === 'active');
  const paid = paidRows.length;
  // tier values are free/paid/premium (no early_access/direct), so per-tier $
  // can't be derived from `tier`. MRR uses a single blended estimate until the
  // Stripe price_id → plan mapping is wired (scaffolded).
  const blendedMrr = paid * PRICE.default;
  const infra  = (learners.filter(l => l.updated_at && Date.now() - new Date(l.updated_at).getTime() < 7 * 86400000).length) * INFRA_PER_ACTIVE;
  return { paid, blendedMrr, infra, margin: blendedMrr - infra, priceTable: PRICE };
}

export function weeklyBuckets(rows, dateKey = 'created_at', weeks = 8) {
  const now = Date.now();
  const buckets = Array.from({ length: weeks }, (_, i) => {
    const end = now - (weeks - 1 - i) * 7 * 86400000;
    return { label: `W${i + 1}`, end, count: 0 };
  });
  rows.forEach(r => {
    const t = new Date(r[dateKey]).getTime();
    for (let i = 0; i < buckets.length; i++) {
      const start = buckets[i].end - 7 * 86400000;
      if (t > start && t <= buckets[i].end) { buckets[i].count++; break; }
    }
  });
  return buckets;
}

export function countBy(rows, key) {
  const m = {};
  rows.forEach(r => { const k = r[key] || '—'; m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).map(([k, n]) => ({ key: k, n })).sort((a, b) => b.n - a.n);
}
